/**
 * User Permissions Context and Hook
 * 
 * Provides permission checking capabilities throughout the app.
 * Use this to conditionally render buttons and actions based on user permissions.
 */

import { createContext, useContext, useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";

const PermissionsContext = createContext(null);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    // Return default permissions if context is not available (e.g., during initial render)
    return {
      permissions: [],
      role: null,
      isSuperAdmin: false,
      loading: true,
      hasPermission: () => false,
      // Tenant capabilities: what the tenant's ONBOARDING MODE allows,
      // independent of the user's role. Defaults to the permissive Intune
      // set so nothing flickers away before /users/me resolves.
      connectionType: "intune",
      capabilities: [],
      hasCapability: () => true,
      isSccmTenant: false,
      canUseIntune: true,
      canUseOSPatch: true,
      serviceTier: null,
      canWriteOSPatch: true,
      canViewOSPatchPolicies: true,
      canManageApps: false,
      canCreatePackages: false,
      canDeployPackages: false,
      canManageOSPatches: false,
      canManageUsers: false,
      canManageSettings: false,
      isViewer: true,
    };
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  // selectedTenantId: /users/me is scoped by the selected tenant, so when
  // the login flow signs the user into an invited tenant (or onboarding
  // registers their own) the role and capabilities must be re-read.
  const { demoMode, getAccessToken, isAuthenticated, selectedTenantId } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [role, setRole] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  // null = not resolved yet. Treated as "everything available" until
  // /users/me answers, so an Intune tenant never sees sections blink out.
  const [capabilities, setCapabilities] = useState(null);
  const [connectionType, setConnectionType] = useState("intune");
  // The tenant's ALICE service tier (full | apps_patch_readonly | apps_only).
  // null = not reported, which keeps OS Patch fully writable.
  const [serviceTier, setServiceTier] = useState(null);

  useEffect(() => {
    const fetchPermissions = async () => {
      if (demoMode) {
        // Demo mode: full admin access
        setPermissions([
          "view_apps", "manage_apps", "create_packages", "deploy_packages",
          "view_os_patches", "manage_os_patches", "view_settings", "manage_settings",
          "manage_users", "view_users", "view_dashboard", "manage_tenant"
        ]);
        setRole("tenant_admin");
        setIsSuperAdmin(false);
        setCapabilities(null);      // demo mode: full Intune capability set
        setConnectionType("intune");
        setServiceTier(null);
        setLoading(false);
        return;
      }

      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const token = await getAccessToken();
        if (!token) {
          setLoading(false);
          return;
        }

        const response = await axios.get(`${API}/users/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        setPermissions(response.data.permissions || []);
        setRole(response.data.role);
        setIsSuperAdmin(response.data.is_super_admin || false);
        // Absent on an older backend -- null keeps every capability, so a
        // frontend deployed ahead of the backend degrades to today's
        // behaviour rather than hiding working features.
        setCapabilities(
          Array.isArray(response.data.capabilities) ? response.data.capabilities : null
        );
        setConnectionType(response.data.connection_type || "intune");
        setServiceTier(response.data.service_tier || null);
      } catch (error) {
        console.error("Failed to fetch permissions:", error);
        // Default to viewer permissions on error
        setPermissions(["view_apps", "view_os_patches", "view_settings", "view_dashboard", "view_users"]);
        setRole("viewer");
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [demoMode, getAccessToken, isAuthenticated, selectedTenantId]);

  // Permission check helper
  const hasPermission = (permission) => {
    return permissions.includes(permission);
  };

  // Capability check. A null capability list means "unknown" -- either the
  // backend predates capabilities or the call has not returned yet -- and
  // resolves permissive, matching the backend guard, which also fails open
  // when it cannot resolve the tenant.
  const hasCapability = (capability) => {
    if (capabilities === null) return true;
    return capabilities.includes(capability);
  };

  // OS Patch write access. Only withheld when the backend names the tenant's
  // service tier AND leaves out `os_patch_write`: a backend that predates
  // service tiers returns capabilities without that entry, and must not hide
  // every OS Patch action from existing tenants.
  const canWriteOSPatch =
    serviceTier === null || capabilities === null
      ? true
      : capabilities.includes("os_patch_write");
  // Update rings, feature and expedited updates, deployments and remediation
  // scripts. The reporting-only plans hold no permission to read them. Same
  // rule as above: only withheld when the backend reports a service tier.
  const canViewOSPatchPolicies =
    serviceTier === null || capabilities === null
      ? true
      : capabilities.includes("os_patch_policies");

  // Computed permission flags for easy access
  const value = {
    permissions,
    role,
    isSuperAdmin,
    loading,
    hasPermission,
    // Specific permission flags
    canManageApps: hasPermission("manage_apps"),
    canCreatePackages: hasPermission("create_packages"),
    canDeployPackages: hasPermission("deploy_packages"),
    canManageOSPatches: hasPermission("manage_os_patches"),
    canManageUsers: hasPermission("manage_users"),
    canManageSettings: hasPermission("manage_settings"),
    canManageTenant: hasPermission("manage_tenant"),
    // Tenant capability flags (onboarding mode, not user role)
    connectionType,
    capabilities: capabilities || [],
    hasCapability,
    isSccmTenant: connectionType === "sccm",
    canUseIntune: hasCapability("intune_deployment"),
    canUseOSPatch: hasCapability("os_patch_management"),
    serviceTier,
    canWriteOSPatch,
    canViewOSPatchPolicies,
    // Role checks
    isViewer: role === "viewer",
    isAdmin: role === "tenant_admin" || isSuperAdmin,
    isAppsManager: role === "apps_manager" || role === "tenant_admin" || isSuperAdmin,
    isOSPatchManager: role === "os_patch_manager" || role === "tenant_admin" || isSuperAdmin,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export default PermissionsProvider;
